import React, { useEffect, useMemo, useState } from 'react';
import jsPDF from 'jspdf';

const LOST_TOOL_TEMPLATE_FALLBACK = {
  classification: 'CUI',
  maintenanceGroup: '',
  checklistNumber: '#507',
  checklistTitle: 'LOST TOOL / LOST ITEM / FOD',
  updatedLabel: '',
  checklistType: 'LOST ITEM',
  checklistRows: [],
};

function deepCloneTemplate(template) {
  return template.map(row => ({ ...row }));
}

function nowLocalInputValue() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function formatDateTimeDisplay(value) {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return `${date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}`;
}

function toLocalInputValue(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function createDefaultLostToolForm(template = LOST_TOOL_TEMPLATE_FALLBACK) {
  return {
    id: '',
    classification: template.classification || 'CUI',
    maintenanceGroup: template.maintenanceGroup || '',
    checklistNumber: template.checklistNumber || '#507',
    checklistTitle: template.checklistTitle || 'LOST TOOL / LOST ITEM / FOD',
    updatedLabel: template.updatedLabel || '',
    controllerName: '',
    controllerEmpNum: '',
    controllerPersonnelId: '',
    notifiedByName: '',
    notifiedByEmpNum: '',
    notifiedByPersonnelId: '',
    lastKnownUserName: '',
    lastKnownUserEmpNum: '',
    lastKnownUserPersonnelId: '',
    reportDateTime: nowLocalInputValue(),
    discoveredAt: '',
    searchStartedAt: '',
    searchCompletedAt: '',
    openedAt: '',
    checklistType: template.checklistType || 'LOST ITEM',
    lostItemDescription: '',
    lastKnownLocation: '',
    caseStatus: 'Searching',
    aircraftInvolved: false,
    tail: '',
    spot: '',
    jcn: '',
    wuc: '',
    notes: '',
    checklistRows: deepCloneTemplate(template.checklistRows || []),
    completedByName: '',
    completedByEmpNum: '',
    completedAt: '',
  };
}

function findRosterPersonForUser(personnelData, userEmail) {
  const normalizedEmail = (userEmail || '').trim().toLowerCase();
  if (!normalizedEmail) return null;
  return (personnelData || []).find(person => (
    String(person.Email || '').trim().toLowerCase() === normalizedEmail
  )) || null;
}

function applyLostToolReporterDefaults(form, rosterPerson) {
  if (!rosterPerson) return form;

  const fullName = `${rosterPerson['First Name'] || ''} ${rosterPerson['Last Name'] || ''}`.trim();
  const empNum = rosterPerson['Emp Num'] || '';
  const personnelId = rosterPerson.id || '';

  return {
    ...form,
    controllerName: form.controllerName || fullName,
    controllerEmpNum: form.controllerEmpNum || empNum,
    controllerPersonnelId: form.controllerPersonnelId || personnelId,
    notifiedByName: form.notifiedByName || fullName,
    notifiedByEmpNum: form.notifiedByEmpNum || empNum,
    notifiedByPersonnelId: form.notifiedByPersonnelId || personnelId,
  };
}

function buildPersonnelOptions(personnelData) {
  return (personnelData || []).map(person => {
    const firstName = person['First Name'] || '';
    const lastName = person['Last Name'] || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return {
      id: person.id,
      fullName,
      empNum: person['Emp Num'] || '',
      rank: person.Rank || '',
      workcenter: person['Shop Name'] || '',
      role: person.Role || '',
      display: [fullName, person['Emp Num'], person.Rank, person['Shop Name']].filter(Boolean).join(' - '),
    };
  });
}

function PersonnelLookupAssist({
  title,
  prefix,
  form,
  setForm,
  personnelOptions,
  includeRank = false,
  includeWorkcenter = false,
}) {
  const [query, setQuery] = useState('');
  const matches = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return [];
    return personnelOptions
      .filter(option => (
        option.fullName.toLowerCase().includes(normalized) ||
        option.empNum.toLowerCase().includes(normalized)
      ))
      .slice(0, 8);
  }, [personnelOptions, query]);

  const applyMatch = (optionId) => {
    const option = personnelOptions.find(candidate => candidate.id === optionId);
    if (!option) return;
    setForm(current => ({
      ...current,
      [`${prefix}Name`]: option.fullName,
      [`${prefix}EmpNum`]: option.empNum,
      [`${prefix}PersonnelId`]: option.id,
      ...(includeRank ? { injuredRank: option.rank || current.injuredRank } : {}),
      ...(includeWorkcenter ? { injuredWorkcenter: option.workcenter || current.injuredWorkcenter } : {}),
    }));
    setQuery('');
  };

  return (
    <div className="safety-lookup">
      <label>{title} Lookup</label>
      <input
        type="search"
        value={query}
        onChange={event => setQuery(event.target.value)}
        placeholder="Search by name or personnel #"
      />
      {matches.length > 0 && (
        <select value="" onChange={event => applyMatch(event.target.value)}>
          <option value="">Select roster match</option>
          {matches.map(option => (
            <option key={option.id} value={option.id}>{option.display}</option>
          ))}
        </select>
      )}
    </div>
  );
}

export default function LostToolTab({ api, user, selectedBase, globalView = false }) {
  const [template, setTemplate] = useState(LOST_TOOL_TEMPLATE_FALLBACK);
  const [form, setForm] = useState(() => createDefaultLostToolForm(LOST_TOOL_TEMPLATE_FALLBACK));
  const [reports, setReports] = useState([]);
  const [personnelData, setPersonnelData] = useState([]);
  const [filterDate, setFilterDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [feedback, setFeedback] = useState('');
  const [saving, setSaving] = useState(false);
  const [templateFeedback, setTemplateFeedback] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  const effectiveLocation = selectedBase || user?.location || '';
  const personnelOptions = useMemo(() => buildPersonnelOptions(personnelData), [personnelData]);
  const currentRosterPerson = useMemo(
    () => findRosterPersonForUser(personnelData, user?.email),
    [personnelData, user?.email]
  );

  const canEditClosed = Boolean(user?.is_superuser || ['Management', 'siteAdmin'].includes(user?.role || ''));
  const canManageTemplate = canEditClosed;
  const checklistMidpoint = Math.ceil(form.checklistRows.length / 2);
  const leftChecklistRows = form.checklistRows.slice(0, checklistMidpoint);
  const rightChecklistRows = form.checklistRows.slice(checklistMidpoint);

  const loadReports = async () => {
    try {
      const res = await api.get('/get-lost-tool-reports', {
        params: {
          email: user.email,
          location: effectiveLocation,
          global: user.is_superuser && globalView ? true : undefined,
          date: filterDate || undefined,
          search: searchTerm || undefined,
        },
      });
      setReports(res.data.data || []);
    } catch (err) {
      console.error('Failed to load lost tool reports', err);
      setReports([]);
      setFeedback(err.response?.data?.message || 'Failed to load lost tool reports.');
    }
  };

  const loadPersonnel = async () => {
    try {
      const res = await api.get('/get-personnel', {
        params: {
          email: user.email,
          location: effectiveLocation,
          global: user.is_superuser && globalView ? true : undefined,
        },
      });
      setPersonnelData(res.data.data || []);
    } catch (err) {
      console.error('Failed to load personnel roster for lookup', err);
    }
  };

  const loadTemplate = async () => {
    try {
      const res = await api.get('/safety-form-template', {
        params: {
          email: user.email,
          location: effectiveLocation,
          global: user.is_superuser && globalView ? true : undefined,
          reportType: 'lost_tool',
        },
      });
      const nextTemplate = { ...LOST_TOOL_TEMPLATE_FALLBACK, ...(res.data.template || {}) };
      setTemplate(nextTemplate);
      setForm(current => (
        current.id
          ? current
          : applyLostToolReporterDefaults(createDefaultLostToolForm(nextTemplate), currentRosterPerson)
      ));
    } catch (err) {
      console.error('Failed to load lost tool template', err);
      setTemplate(LOST_TOOL_TEMPLATE_FALLBACK);
      setForm(applyLostToolReporterDefaults(createDefaultLostToolForm(LOST_TOOL_TEMPLATE_FALLBACK), currentRosterPerson));
      setFeedback(err.response?.data?.message || 'Failed to load lost tool base template.');
    }
  };

  useEffect(() => {
    setTemplate(LOST_TOOL_TEMPLATE_FALLBACK);
    setForm(createDefaultLostToolForm(LOST_TOOL_TEMPLATE_FALLBACK));
    setReports([]);
    setPersonnelData([]);
    setFeedback('');
    setTemplateFeedback('');
  }, [effectiveLocation]);

  useEffect(() => {
    loadReports();
    loadPersonnel();
  }, [effectiveLocation, filterDate, searchTerm, globalView, user.email, user.is_superuser]);

  useEffect(() => {
    loadTemplate();
  }, [effectiveLocation, globalView, user.email, user.is_superuser]);

  useEffect(() => {
    setForm(current => {
      if (current.id || current.controllerName || current.notifiedByName) {
        return current;
      }
      return applyLostToolReporterDefaults(current, currentRosterPerson);
    });
  }, [currentRosterPerson]);

  const loadReportIntoForm = (report) => {
    setForm({
      ...applyLostToolReporterDefaults(createDefaultLostToolForm(template), currentRosterPerson),
      ...report,
      reportDateTime: toLocalInputValue(report.reportDateTime),
      discoveredAt: toLocalInputValue(report.discoveredAt),
      searchStartedAt: toLocalInputValue(report.searchStartedAt),
      searchCompletedAt: toLocalInputValue(report.searchCompletedAt),
      openedAt: toLocalInputValue(report.openedAt),
      completedAt: toLocalInputValue(report.completedAt),
      checklistRows: report.checklistRows?.length ? report.checklistRows.map(row => ({ ...row })) : deepCloneTemplate(template.checklistRows || []),
    });
  };

  const resetForm = () => {
    setForm(applyLostToolReporterDefaults(createDefaultLostToolForm(template), currentRosterPerson));
    setFeedback('');
  };

  const updateChecklistRow = (index, field, value) => {
    setForm(current => ({
      ...current,
      checklistRows: current.checklistRows.map((row, rowIndex) => (
        rowIndex === index ? { ...row, [field]: value } : row
      )),
    }));
  };

  const addChecklistRow = () => {
    setForm(current => ({
      ...current,
      checklistRows: [
        ...current.checklistRows,
        { contact: '', phone: '', note: '', personNotified: '', time: '', method: '' },
      ],
    }));
  };

  const removeChecklistRow = (index) => {
    setForm(current => ({
      ...current,
      checklistRows: current.checklistRows.filter((_, rowIndex) => rowIndex !== index),
    }));
  };

  const searchDurationMinutes = useMemo(() => {
    if (!form.searchStartedAt || !form.reportDateTime) return null;
    const start = new Date(form.searchStartedAt);
    const report = new Date(form.reportDateTime);
    if (Number.isNaN(start.getTime()) || Number.isNaN(report.getTime())) return null;
    return Math.round((report.getTime() - start.getTime()) / 60000);
  }, [form.searchStartedAt, form.reportDateTime]);

  const saveReport = async () => {
    if (!form.lostItemDescription.trim()) {
      setFeedback('Lost item description is required.');
      return;
    }
    if (!form.searchStartedAt) {
      setFeedback('Search start time is required before opening a lost tool report.');
      return;
    }
    if (searchDurationMinutes !== null && searchDurationMinutes < 60 && !form.id) {
      setFeedback('A 60-minute search is required before opening a lost tool report.');
      return;
    }
    if (form.closedAt && !canEditClosed) {
      setFeedback('Closed reports may only be edited by Management, siteAdmin, or a superuser.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        email: user.email,
        location: effectiveLocation,
      };
      const res = await api.post('/save-lost-tool-report', payload);
      setFeedback(res.data.message || 'Lost tool report saved.');
      resetForm();
      await loadReports();
    } catch (err) {
      console.error('Failed to save lost tool report', err);
      setFeedback(err.response?.data?.message || 'Failed to save lost tool report.');
    } finally {
      setSaving(false);
    }
  };

  const saveTemplate = async () => {
    if (!canManageTemplate) return;
    setSavingTemplate(true);
    try {
      const payload = {
        email: user.email,
        location: effectiveLocation,
        reportType: 'lost_tool',
        classification: form.classification,
        maintenanceGroup: form.maintenanceGroup,
        checklistNumber: form.checklistNumber,
        checklistTitle: form.checklistTitle,
        updatedLabel: form.updatedLabel,
        checklistType: form.checklistType,
        checklistRows: form.checklistRows,
      };
      const res = await api.post('/save-safety-form-template', payload);
      const nextTemplate = { ...LOST_TOOL_TEMPLATE_FALLBACK, ...(res.data.template || {}) };
      setTemplate(nextTemplate);
      setTemplateFeedback(res.data.message || 'Lost tool template saved.');
    } catch (err) {
      console.error('Failed to save lost tool template', err);
      setTemplateFeedback(err.response?.data?.message || 'Failed to save lost tool template.');
    } finally {
      setSavingTemplate(false);
    }
  };

  const exportReportToPDF = (report) => {
    const doc = new jsPDF('p', 'mm', 'letter');
    let y = 12;

    doc.setFontSize(14);
    doc.text('#507 LOST TOOL / LOST ITEM', 14, y);
    y += 8;
    doc.setFontSize(10);
    [
      `Controller: ${report.controllerName || '--'} (${report.controllerEmpNum || '--'})`,
      `Notified By: ${report.notifiedByName || '--'} (${report.notifiedByEmpNum || '--'})`,
      `Date/Time: ${formatDateTimeDisplay(report.reportDateTime)}`,
      `Tail / Spot: ${report.tail || '--'} / ${report.spot || '--'}`,
      `Status: ${report.caseStatus || '--'}`,
      `Description: ${report.lostItemDescription || '--'}`,
      `Last Known Location: ${report.lastKnownLocation || '--'}`,
      `JCN: ${report.jcn || '--'}   WUC: ${report.wuc || '--'}`,
    ].forEach(line => {
      doc.text(line, 14, y);
      y += 6;
    });

    y += 4;
    doc.setFontSize(11);
    doc.text('Notification Checklist', 14, y);
    y += 6;
    doc.setFontSize(9);
    (report.checklistRows || []).forEach(row => {
      if (y > 260) {
        doc.addPage();
        y = 14;
      }
      const line = `${row.contact || '--'} | ${row.phone || '--'} | ${row.personNotified || '--'} | ${row.time || '--'}`;
      doc.text(line, 14, y);
      y += 5;
    });

    doc.save(`lost_tool_${report.id || 'report'}.pdf`);
  };

  return (
    <div className="safety-reports">
      <div className="reports-panel">
        <div className="reports-panel__header">
          <h3>Lost Tool</h3>
        </div>

        {feedback ? <div className="safety-feedback">{feedback}</div> : null}
        {searchDurationMinutes !== null && !form.id && searchDurationMinutes < 60 ? (
          <div className="safety-warning">
            Search duration currently shows {searchDurationMinutes} minutes. A 60-minute search is required before opening a lost tool case.
          </div>
        ) : null}
        {form.aircraftInvolved ? (
          <div className="safety-warning">
            Aircraft involvement selected. Enter the JCN and ensure the aircraft forms reflect the lost tool / lost item red-X requirement.
          </div>
        ) : null}

        <div className="safety-static-header">
          <div className="safety-static-card">
            <label>Classification</label>
            <strong>{form.classification || 'CUI'}</strong>
          </div>
          <div className="safety-static-card">
            <label>Maintenance Group</label>
            <strong>{form.maintenanceGroup || '--'}</strong>
          </div>
          <div className="safety-static-card safety-static-card--accent">
            <label>Checklist</label>
            <strong>{form.checklistNumber || '#507'}</strong>
            <span>{form.checklistTitle || 'LOST TOOL / LOST ITEM / FOD'}</span>
          </div>
          <div className="safety-static-card">
            <label>Checklist Type</label>
            <strong>{form.checklistType || 'LOST ITEM'}</strong>
          </div>
          <div className="safety-static-card">
            <label>Updated</label>
            <strong>{form.updatedLabel || '--'}</strong>
          </div>
        </div>

        {canManageTemplate ? (
          <div className="safety-template-toolbar">
            <label>
              <span>Base Template Maintenance Group</span>
              <input value={form.maintenanceGroup} onChange={e => setForm({ ...form, maintenanceGroup: e.target.value })} />
            </label>
            <label>
              <span>Template Updated Label</span>
              <input value={form.updatedLabel} onChange={e => setForm({ ...form, updatedLabel: e.target.value })} />
            </label>
            <button type="button" className="safety-template-toolbar__button" onClick={saveTemplate} disabled={savingTemplate}>
              {savingTemplate ? 'Saving Template...' : `Save ${effectiveLocation || 'Base'} Template`}
            </button>
            {templateFeedback ? <span className="safety-template-toolbar__feedback">{templateFeedback}</span> : null}
          </div>
        ) : null}

        <div className="safety-grid safety-grid--lost-tool-top">
          <div className="safety-panel-stack">
            <div className="safety-subpanel safety-subpanel--compact-form">
              <h4>Controller</h4>
              <PersonnelLookupAssist title="Controller" prefix="controller" form={form} setForm={setForm} personnelOptions={personnelOptions} />
              <label><span>Name</span><input value={form.controllerName} onChange={e => setForm({ ...form, controllerName: e.target.value })} /></label>
              <label><span>Personnel #</span><input value={form.controllerEmpNum} onChange={e => setForm({ ...form, controllerEmpNum: e.target.value })} /></label>
            </div>

            <div className="safety-subpanel safety-subpanel--compact-form">
              <h4>Notified By</h4>
              <PersonnelLookupAssist title="Notified By" prefix="notifiedBy" form={form} setForm={setForm} personnelOptions={personnelOptions} />
              <label><span>Name</span><input value={form.notifiedByName} onChange={e => setForm({ ...form, notifiedByName: e.target.value })} /></label>
              <label><span>Personnel #</span><input value={form.notifiedByEmpNum} onChange={e => setForm({ ...form, notifiedByEmpNum: e.target.value })} /></label>
            </div>
          </div>

          <div className="safety-subpanel">
            <h4>Last Known User</h4>
            <PersonnelLookupAssist title="Last Known User" prefix="lastKnownUser" form={form} setForm={setForm} personnelOptions={personnelOptions} />
            <label><span>Name</span><input value={form.lastKnownUserName} onChange={e => setForm({ ...form, lastKnownUserName: e.target.value })} /></label>
            <label><span>Personnel #</span><input value={form.lastKnownUserEmpNum} onChange={e => setForm({ ...form, lastKnownUserEmpNum: e.target.value })} /></label>
            <label><span>Notes / Actions</span><textarea rows={4} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></label>
          </div>

          <div className="safety-subpanel">
            <h4>Case Timing</h4>
            <div className="safety-grid safety-grid--timing">
              <label><span>Report Date/Time</span><input type="datetime-local" value={form.reportDateTime} onChange={e => setForm({ ...form, reportDateTime: e.target.value })} /></label>
              <label><span>Missing Discovered</span><input type="datetime-local" value={form.discoveredAt} onChange={e => setForm({ ...form, discoveredAt: e.target.value })} /></label>
              <label><span>Search Started</span><input type="datetime-local" value={form.searchStartedAt} onChange={e => setForm({ ...form, searchStartedAt: e.target.value })} /></label>
              <label><span>Search Completed</span><input type="datetime-local" value={form.searchCompletedAt} onChange={e => setForm({ ...form, searchCompletedAt: e.target.value })} /></label>
              <label><span>Checklist Completed By</span><input value={form.completedByName} onChange={e => setForm({ ...form, completedByName: e.target.value })} /></label>
              <label><span>Checklist Completed By Man #</span><input value={form.completedByEmpNum} onChange={e => setForm({ ...form, completedByEmpNum: e.target.value })} /></label>
              <label><span>Checklist Completed At</span><input type="datetime-local" value={form.completedAt} onChange={e => setForm({ ...form, completedAt: e.target.value })} /></label>
            </div>
          </div>

          <div className="safety-subpanel">
            <h4>Lost Tool Details</h4>
            <div className="safety-grid safety-grid--status-row">
              <label className="safety-field--status"><span>Case Status</span>
                <select value={form.caseStatus} onChange={e => setForm({ ...form, caseStatus: e.target.value })}>
                  <option value="Searching">Searching</option>
                  <option value="Reported">Reported</option>
                  <option value="Closed - Found">Closed - Found</option>
                  <option value="Closed - Not Found">Closed - Not Found</option>
                </select>
              </label>
              <div className="safety-subpanel safety-subpanel--compact">
                <h4>Workflow Meaning</h4>
                <p className="safety-subpanel__text">Use <strong>Searching</strong> while the 60-minute search is underway, <strong>Reported</strong> after the search is unsuccessfully completed, and the two <strong>Closed</strong> states when the item is either found or not found.</p>
              </div>
            </div>
            <div className="safety-grid safety-grid--two">
              <label><span>Lost Item Description</span><textarea rows={2} value={form.lostItemDescription} onChange={e => setForm({ ...form, lostItemDescription: e.target.value })} /></label>
              <label><span>Last Known Location</span><textarea rows={2} value={form.lastKnownLocation} onChange={e => setForm({ ...form, lastKnownLocation: e.target.value })} /></label>
            </div>
            <div className="safety-grid safety-grid--aircraft">
              <label className="safety-field--short"><span>Aircraft Involved</span><select value={form.aircraftInvolved ? 'Yes' : 'No'} onChange={e => setForm({ ...form, aircraftInvolved: e.target.value === 'Yes' })}><option>Yes</option><option>No</option></select></label>
              <label className={`safety-field--compact ${!form.aircraftInvolved ? 'safety-field--disabled' : ''}`}><span>Tail #</span><input disabled={!form.aircraftInvolved} value={form.tail} onChange={e => setForm({ ...form, tail: e.target.value })} /></label>
              <label className={`safety-field--compact ${!form.aircraftInvolved ? 'safety-field--disabled' : ''}`}><span>Spot</span><input disabled={!form.aircraftInvolved} value={form.spot} onChange={e => setForm({ ...form, spot: e.target.value })} /></label>
              <label className={`safety-field--compact ${!form.aircraftInvolved ? 'safety-field--disabled' : ''}`}><span>JCN</span><input disabled={!form.aircraftInvolved} value={form.jcn} onChange={e => setForm({ ...form, jcn: e.target.value })} /></label>
              <label className={`safety-field--compact ${!form.aircraftInvolved ? 'safety-field--disabled' : ''}`}><span>WUC</span><input disabled={!form.aircraftInvolved} value={form.wuc} onChange={e => setForm({ ...form, wuc: e.target.value })} /></label>
            </div>
          </div>
        </div>

        <div className="safety-actions safety-actions--details-row">
          <button type="button" onClick={saveReport} disabled={saving}>{saving ? 'Saving...' : form.id ? 'Update Lost Tool Form' : 'Submit Lost Tool Form'}</button>
          <button type="button" onClick={resetForm}>Clear Form</button>
          {form.id ? <button type="button" onClick={() => exportReportToPDF(form)}>Export Current Report</button> : null}
        </div>

        <div className="safety-grid safety-grid--case-layout">
          <div className="safety-subpanel">
            <h4>Notification Checklist</h4>
            <div className="safety-checklist-grid">
              <div className="reports-table-shell">
                <table className="reports-table">
                  <thead>
                    <tr>
                      <th>Contact</th>
                      <th>Phone / Note</th>
                      <th>Person Notified</th>
                      <th>Time</th>
                      {canManageTemplate ? <th>Template</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {leftChecklistRows.map((row, index) => (
                      <React.Fragment key={`${row.contact}-${index}`}>
                        <tr>
                          <td>
                            {canManageTemplate ? (
                              <input value={row.contact || ''} onChange={e => updateChecklistRow(index, 'contact', e.target.value)} placeholder="Contact" />
                            ) : row.contact}
                          </td>
                          <td>
                            {canManageTemplate ? (
                              <input value={row.phone || ''} onChange={e => updateChecklistRow(index, 'phone', e.target.value)} placeholder="Phone / routing" />
                            ) : (
                              <>
                                <div>{row.phone}</div>
                                {row.note ? <div className="safety-table-note">{row.note}</div> : null}
                              </>
                            )}
                          </td>
                          <td><input value={row.personNotified || ''} onChange={e => updateChecklistRow(index, 'personNotified', e.target.value)} /></td>
                          <td><input value={row.time || ''} onChange={e => updateChecklistRow(index, 'time', e.target.value)} placeholder="HHMM / HH:MM" /></td>
                          {canManageTemplate ? (
                            <td><button type="button" className="safety-table-action" onClick={() => removeChecklistRow(index)}>Remove</button></td>
                          ) : null}
                        </tr>
                        {canManageTemplate ? (
                          <tr className="safety-table-note-row">
                            <td colSpan={3}>
                              <input
                                className="safety-checklist-note-input"
                                value={row.note || ''}
                                onChange={e => updateChecklistRow(index, 'note', e.target.value)}
                                placeholder="Optional note"
                              />
                            </td>
                            <td colSpan={canManageTemplate ? 2 : 1}></td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="reports-table-shell">
                <table className="reports-table">
                  <thead>
                    <tr>
                      <th>Contact</th>
                      <th>Phone / Note</th>
                      <th>Person Notified</th>
                      <th>Time</th>
                      {canManageTemplate ? <th>Template</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {rightChecklistRows.map((row, index) => {
                      const rowIndex = checklistMidpoint + index;
                      return (
                        <React.Fragment key={`${row.contact}-${rowIndex}`}>
                          <tr>
                            <td>
                              {canManageTemplate ? (
                                <input value={row.contact || ''} onChange={e => updateChecklistRow(rowIndex, 'contact', e.target.value)} placeholder="Contact" />
                              ) : row.contact}
                            </td>
                            <td>
                              {canManageTemplate ? (
                                <input value={row.phone || ''} onChange={e => updateChecklistRow(rowIndex, 'phone', e.target.value)} placeholder="Phone / routing" />
                              ) : (
                                <>
                                  <div>{row.phone}</div>
                                  {row.note ? <div className="safety-table-note">{row.note}</div> : null}
                                </>
                              )}
                            </td>
                            <td><input value={row.personNotified || ''} onChange={e => updateChecklistRow(rowIndex, 'personNotified', e.target.value)} /></td>
                            <td><input value={row.time || ''} onChange={e => updateChecklistRow(rowIndex, 'time', e.target.value)} placeholder="HHMM / HH:MM" /></td>
                            {canManageTemplate ? (
                              <td><button type="button" className="safety-table-action" onClick={() => removeChecklistRow(rowIndex)}>Remove</button></td>
                            ) : null}
                          </tr>
                          {canManageTemplate ? (
                            <tr className="safety-table-note-row">
                              <td colSpan={3}>
                                <input
                                  className="safety-checklist-note-input"
                                  value={row.note || ''}
                                  onChange={e => updateChecklistRow(rowIndex, 'note', e.target.value)}
                                  placeholder="Optional note"
                                />
                              </td>
                              <td colSpan={canManageTemplate ? 2 : 1}></td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            {canManageTemplate ? (
              <div className="safety-actions safety-actions--inline">
                <button type="button" onClick={addChecklistRow}>Add Template Contact Row</button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="reports-panel">
        <div className="reports-panel__header">
          <h3>Previous Lost Tool Cases</h3>
          <span>Search open and closed cases</span>
        </div>
        <div className="safety-filter-row">
          <label><span>Submitted Date</span><input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)} /></label>
          <label className="safety-filter-row__search"><span>Search</span><input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Tail, item, JCN, status" /></label>
        </div>
        <div className="reports-table-shell">
          <table className="reports-table">
            <thead>
              <tr>
                <th>Date/Time</th>
                <th>Tail</th>
                <th>Item</th>
                <th>Status</th>
                <th>Controller</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.map(report => (
                <tr key={report.id}>
                  <td>{formatDateTimeDisplay(report.reportDateTime)}</td>
                  <td>{report.tail || '--'}</td>
                  <td>{report.lostItemDescription || '--'}</td>
                  <td>{report.caseStatus || '--'}</td>
                  <td>{report.controllerName || '--'}</td>
                  <td className="status-table__actions">
                    <button type="button" onClick={() => loadReportIntoForm(report)}>Edit</button>
                    <button type="button" onClick={() => exportReportToPDF(report)}>PDF</button>
                  </td>
                </tr>
              ))}
              {reports.length === 0 && (
                <tr>
                  <td colSpan={6}>No lost tool reports found for the current filters.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
